import React, { useEffect, useMemo, useState } from 'react';
import { getTrades, getSignals, getHealth, getTradovateHealth } from '../api';
import { ClassBadge, EntryModeBadge, QualityScore } from './ClassBadge';
import { formatTradeTime } from '../utils/time';
import { pnlColorClass } from '../utils/pnl';
import { Inbox, Loader2 } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { KpiCard } from './ui/KpiCard';
import { EmptyState } from './ui/EmptyState';
import { TableWrapper, Thead, Th, Tbody, Tr, Td } from './ui/TableWrapper';
import { InstrumentCell } from './ui/InstrumentCell';
import {
  BarChart, Bar, XAxis, YAxis, ReferenceLine,
  LineChart, Line, AreaChart, Area,
  ResponsiveContainer, Tooltip as RTooltip, Cell,
} from 'recharts';

const DEFAULT_COMMISSION_PER_RT = 5.82;

const formatDuration = (seconds: number) => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const ScoreHeaderTooltip = () => (
  <span className="relative group cursor-help inline-flex items-center gap-1">
    Score <span className="text-gray-500 text-xs">ⓘ</span>
    <span className="hidden group-hover:block absolute z-20 bg-gray-800 text-xs text-white p-2 rounded w-48 top-6 left-0 whitespace-pre-line normal-case">
      75+ SMR confirmed{'\n'}
      50+ Macro + Liquidity{'\n'}
      30+ Macro only{'\n'}
      5+ Breaker only
    </span>
  </span>
);

const PAGE_SIZE_KEY = 'trades_page_size';
const PAGE_SIZES = [25, 50, 100];

const toneForPnl = (v: number): 'positive' | 'negative' | 'neutral' => v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral';

export default function Trades() {
  const [sortField, setSortField] = useState<string>('open_time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (f: string) => { if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('desc'); } };
  const SortIndicator = ({ field }: { field: string }) => sortField === field ? <span className="text-blue-400 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  const [trades, setTrades] = useState<any[]>([]);
  const [orderIdMap, setOrderIdMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [tvHealth, setTvHealth] = useState<any>(null);
  const [classFilter, setClassFilter] = useState<string>('all');
  const [showSystemTrades, setShowSystemTrades] = useState<boolean>(false);

  const initialPageSize = (() => {
    const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(stored) ? stored : 50;
  })();
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    const load = async () => {
      try {
        const [t, s, h, tv] = await Promise.all([getTrades(), getSignals(), getHealth(), getTradovateHealth()]);
        // Dashboard-accuracy fix (2026-07-15): today_pnl here was a balance
        // delta (totalCashValue - SOD) that INCLUDES unrealized/open P&L --
        // a different basis than the Dashboard tab's "Today P&L" (broker
        // session-realized) and a third definition on /health (DB, closed
        // trades, ET midnight). Standardized on today_pnl_db everywhere.
        if (tv?.data?.today_pnl_db !== undefined) setTvHealth({ ...tv.data, today_pnl: tv.data.today_pnl_db });
        else if (h?.data?.today_pnl !== undefined) setTvHealth(h.data);
        setTrades(t.data);
        const map: Record<string, string> = {};
        for (const sig of s.data as any[]) {
          if (sig.id && sig.tradovate_order_id) map[sig.id] = sig.tradovate_order_id;
        }
        setOrderIdMap(map);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const isOrphan = (t: any) => t.signal_class === 'ORPHAN' || t.signal_class === 'ORPHAN_DUPLICATE';

  const filtered = trades.filter(t => classFilter === 'all' || t.signal_class === classFilter);
  // KPIs and Equity Curve always exclude orphans -- they distort P&L stats.
  const filteredClosed = filtered.filter(t => t.status === 'closed' && !isOrphan(t));

  const totalPnl = filteredClosed.reduce((sum, t) => sum + (t.net_pnl ?? t.pnl ?? 0), 0);
  const winTrades = filteredClosed.filter(t => (t.net_pnl ?? t.pnl ?? 0) > 0);
  const lossTrades = filteredClosed.filter(t => (t.net_pnl ?? t.pnl ?? 0) < 0);
  const wins = winTrades.length;
  const losses = lossTrades.length;
  const total = filteredClosed.length;
  const winRate = total > 0 ? (wins / total * 100) : 0;
  const avgWin = wins > 0 ? winTrades.reduce((s, t) => s + (t.net_pnl ?? t.pnl ?? 0), 0) / wins : 0;
  const avgLoss = losses > 0 ? lossTrades.reduce((s, t) => s + (t.net_pnl ?? t.pnl ?? 0), 0) / losses : 0;

  const totalCommission = filteredClosed.reduce(
    (sum, t) => sum + (t.commission_usd ?? DEFAULT_COMMISSION_PER_RT * (t.quantity || 1)),
    0
  );

  // Table rows: respect orphan toggle on top of class filter.
  const tableRows = filtered.filter(t => showSystemTrades || !isOrphan(t));
  const hiddenOrphanCount = filtered.filter(t => isOrphan(t)).length;

  const sorted = useMemo(() => {
    return [...tableRows].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null) return 1; if (bv == null) return -1;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [tableRows, sortField, sortDir]);

  const totalRows = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalRows);
  const pageRows = sorted.slice(startIdx, endIdx);

  const winRateTone: 'positive' | 'negative' | 'neutral' | 'default' =
    total === 0 ? 'neutral' : winRate >= 55 ? 'positive' : winRate >= 40 ? 'default' : 'negative';

  if (loading) return (
    <div>
      <PageHeader title="Trades" description="Live trade history. All figures are live fills only -- no paper trades." />
      <div className="flex flex-col items-center justify-center h-48 text-gray-500 gap-3">
        <Loader2 size={28} className="animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Trades"
        description="Live trade history. All figures are live fills only -- no paper trades."
      />

      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Today P&L"
            value={tvHealth?.today_pnl != null ? `${tvHealth.today_pnl > 0 ? '+' : ''}$${Number(tvHealth.today_pnl).toFixed(2)}` : `${totalPnl > 0 ? '+' : ''}$${totalPnl.toFixed(2)}`}
            tone={toneForPnl(tvHealth?.today_pnl ?? totalPnl)}
            sub={
              <>
                <span>{tvHealth?.today_pnl != null ? `Tradovate · SOD $${Number(tvHealth.sod_balance || 0).toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2})}` : `${total} closed trades`}</span>
                {tvHealth?.today_pnl == null && <span className="block text-gray-400">Commission: -${totalCommission.toFixed(2)}</span>}
              </>
            }
          />
          <KpiCard
            label="Win Rate"
            value={total > 0 ? `${winRate.toFixed(1)}%` : '--'}
            tone={winRateTone}
            sub={`${wins}W / ${losses}L`}
          />
          <KpiCard
            label="Avg Win"
            value={wins > 0 ? `$${avgWin.toFixed(2)}` : '--'}
            tone={wins > 0 ? 'positive' : 'neutral'}
          />
          <KpiCard
            label="Avg Loss"
            value={losses > 0 ? `$${avgLoss.toFixed(2)}` : '--'}
            tone={losses > 0 ? 'negative' : 'neutral'}
          />
        </div>

        {/* Cumulative P&L + Win/Loss histogram charts */}
        <TradesCharts trades={filteredClosed} />

        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-base font-semibold text-white">Trade History</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${showSystemTrades ? 'text-gray-300' : 'text-gray-500'}`}>
                <input
                  type="checkbox"
                  checked={showSystemTrades}
                  onChange={e => { setShowSystemTrades(e.target.checked); setPage(1); }}
                />
                Show system trades
              </label>
              <select value={classFilter} onChange={e => { setClassFilter(e.target.value); setPage(1); }}
                className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
                <option value="all">All Classes</option>
                <option value="SMR">SMR</option>
                <option value="MACRO_LIQ">MACRO_LIQ</option>
                <option value="MACRO_ONLY">MACRO_ONLY</option>
                <option value="ORPHAN">ORPHAN</option>
              </select>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
                {PAGE_SIZES.map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 px-1">
            <span><span className="font-medium text-gray-400">MFE</span> — Max Favorable Excursion (peak unrealized profit)</span>
            <span><span className="font-medium text-gray-400">MAE</span> — Max Adverse Excursion (peak unrealized loss)</span>
            <span><span className="font-medium text-gray-400">Hold</span> — Time in trade</span>
            <span><span className="font-medium text-gray-400">Exit</span> — scaled = partial scale-out / simple = all-at-once</span>
          </div>

          <div className="overflow-y-auto max-h-[600px]">
            <TableWrapper>
              <Thead>
                <Th onClick={() => handleSort('open_time')}>Time <SortIndicator field="open_time" /></Th>
                <Th onClick={() => handleSort('instrument')}>Inst <SortIndicator field="instrument" /></Th>
                <Th onClick={() => handleSort('signal_class')}>Class <SortIndicator field="signal_class" /></Th>
                <Th onClick={() => handleSort('direction')}>Dir <SortIndicator field="direction" /></Th>
                <Th>Entry</Th>
                <Th>Exit</Th>
                <Th onClick={() => handleSort('quantity')}>Qty <SortIndicator field="quantity" /></Th>
                <Th><ScoreHeaderTooltip /></Th>
                <Th onClick={() => handleSort('time_in_trade_seconds')}>Hold <SortIndicator field="time_in_trade_seconds" /></Th>
                <Th onClick={() => handleSort('mfe')} title="Max Favorable Excursion">MFE <SortIndicator field="mfe" /></Th>
                <Th onClick={() => handleSort('mae')} title="Max Adverse Excursion">MAE <SortIndicator field="mae" /></Th>
                <Th onClick={() => handleSort('exit_strategy')}>Exit <SortIndicator field="exit_strategy" /></Th>
                <Th>Order ID</Th>
                <Th onClick={() => handleSort('pnl')}>P&L <SortIndicator field="pnl" /></Th>
              </Thead>
              <Tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="px-3 py-6">
                      <EmptyState icon={<Inbox size={28} />} message="No trades yet" sub="Closed and open trades will appear here." />
                    </td>
                  </tr>
                ) : pageRows.map((t: any) => {
                  const pnlVal = t.status === 'open' ? (t.unrealized_pnl ?? 0) : (t.net_pnl ?? t.pnl ?? 0);
                  return (
                    <Tr key={t.id}>
                      <Td className="text-gray-400 text-xs">{formatTradeTime(t.open_time || t.created_at)}</Td>
                      <Td className="font-medium"><InstrumentCell symbol={t.instrument} /></Td>
                      <Td className="whitespace-nowrap"><ClassBadge signalClass={t.signal_class} /><EntryModeBadge mode={t.entry_mode} /></Td>
                      <Td><DirectionBadge dir={t.direction} /></Td>
                      <Td className="font-mono text-xs">{t.entry_price || '--'}</Td>
                      <Td className="font-mono text-xs">{t.exit_price || '--'}</Td>
                      <Td>{t.quantity}</Td>
                      <Td><QualityScore score={t.quality_score} /></Td>
                      <Td className="font-mono text-xs text-gray-400">{formatDuration(t.time_in_trade_seconds)}</Td>
                      <Td className="font-mono text-xs text-green-400">${(t.mfe || 0).toFixed(0)}</Td>
                      <Td className="font-mono text-xs text-red-400">${(t.mae || 0).toFixed(0)}</Td>
                      <Td>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.exit_strategy === 'simple' ? 'bg-blue-900 text-blue-300' : 'bg-purple-900 text-purple-300'}`}
                          title={t.exit_strategy === 'simple' ? '50% at 2.0 STDV + BE stop, 50% runner to 4.0' : '40% at 2.0 + BE, 20% at 2.5, 20% at 4.0, 20% runner to 4.5'}
                        >
                          {t.exit_strategy || '--'}
                        </span>
                      </Td>
                      <Td className="font-mono text-xs text-gray-400">{orderIdMap[t.signal_id] || '--'}</Td>
                      <Td className={`font-mono font-bold ${pnlColorClass(pnlVal)}`}>
                        {t.status === 'open' ? `$${pnlVal.toFixed(2)}` : `${pnlVal > 0 ? '+' : ''}$${pnlVal.toFixed(2)}`}
                        {t.status === 'open' && <span className="text-gray-500 text-xs ml-1">live</span>}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </TableWrapper>
          </div>

          {!showSystemTrades && hiddenOrphanCount > 0 && (
            <div className="text-xs text-gray-500 px-1">
              ⊘ {hiddenOrphanCount} system trade{hiddenOrphanCount === 1 ? '' : 's'} hidden (ORPHAN / ORPHAN_DUPLICATE) —{' '}
              <button
                onClick={() => { setShowSystemTrades(true); setPage(1); }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Show
              </button>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
            <span>{totalRows === 0 ? 'No trades' : `Showing ${startIdx + 1}-${endIdx} of ${totalRows} trades`}</span>
            <div className="flex items-center gap-2">
              <button
                disabled={safePage <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >Prev</button>
              <span>Page {safePage} / {pageCount}</span>
              <button
                disabled={safePage >= pageCount}
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DirectionBadge({ dir }: { dir?: string }) {
  if (!dir) return null;
  const cls = dir === 'long' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {dir.toUpperCase()}
    </span>
  );
}

// NOTE: STATUS column was removed -- backend exit_type is always 'none', so the
// column added no signal. Reintroduce once exit_type is populated (TARGET_HIT /
// STOP_LOSS / MANUAL).

export function TradesCharts({ trades }: { trades: any[] }) {
  const equityCurve = useMemo(() => {
    const closed = trades
      .filter(t => t.close_time && t.net_pnl != null)
      .sort((a, b) => new Date(a.close_time).getTime() - new Date(b.close_time).getTime());
    let running = 0;
    return closed.map((t, i) => {
      running += Number(t.net_pnl);
      const d = new Date(t.close_time);
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      return { index: i + 1, label, cumPnl: Math.round(running * 100) / 100 };
    });
  }, [trades]);

  const isPositive = equityCurve.length > 0 && equityCurve[equityCurve.length - 1].cumPnl >= 0;
  const lineColor = isPositive ? '#4ade80' : '#f87171';
  const fillColor = isPositive ? '#4ade8022' : '#f8717122';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider">Equity Curve</span>
        {equityCurve.length > 0 && (
          <span className={`text-xs font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}{equityCurve[equityCurve.length - 1].cumPnl.toFixed(2)}
          </span>
        )}
      </div>
      {equityCurve.length === 0 ? (
        <div className="text-xs text-gray-600 py-6 text-center">No closed trades yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={equityCurve} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
            <defs>
              <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={lineColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={lineColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6b7280' }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(v) => `$${v}`} />
            <RTooltip
              formatter={(v: any) => [`$${Number(v).toFixed(2)}`, 'Cumulative P&L'] as [string, string]}
              labelFormatter={(l) => `Trade ${l}`}
              contentStyle={{ background: '#1f2937', border: '1px solid #374151', fontSize: 12 }}
            />
            <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="cumPnl"
              stroke={lineColor}
              strokeWidth={2}
              fill="url(#equityGrad)"
              dot={false}
              activeDot={{ r: 3, fill: lineColor }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
